import React from 'react'
import { connect } from 'react-redux'
import { Stage, Layer } from 'react-konva';
import PartImage from './PartImage'
import * as actions from '../actions'
import { AppState, ImagePosition, ImageData } from '../reducers';
import { AppMode } from '../constants';
import Konva from 'konva';
import { Product, getSelectedImagePath } from '../services/assortment.service';

function boundingRect(prds: Product[], images: Map<string, ImageData>) {
	let minX = 1e9
	let maxX = -1e9
	let minY = 1e9
	let maxY = -1e9

	for (const prd of prds) {
		const imgConf = actions.createImageConfigForProduct(prd)
		const img = images.get(imgConf.path)
		if (!img) continue

		minX = Math.min(minX, imgConf.img_pos.x)
		maxX = Math.max(maxX, imgConf.img_pos.x + img.origWidth)
		minY = Math.min(minY, imgConf.img_pos.y)
		maxY = Math.max(maxY, imgConf.img_pos.y + img.origHeight)
	}

	return { width: maxX - minX, height: maxY - minY }
}

interface PartViewState {
	dataWidth: number
	dataHeight: number
	stageWidth: number
	stageHeight: number
}

class PartView extends React.Component<ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>, PartViewState> {
	stage: Konva.Stage
	stageWrapper: HTMLDivElement

	constructor(props) {
		super(props)

		const bounds = boundingRect(props.productsToShow, props.images)

		this.onWheel = this.onWheel.bind(this)

		this.state = {
			dataWidth: bounds.width,
			dataHeight: bounds.height,
			stageWidth: 350,
			stageHeight: 350
		}
	}

	onWheel(e) {
		e.preventDefault()

		this.setState((state, props) => {
			const step = Math.sign(e.deltaY) * props.mouseMultiplier

			const newDimensions = {
				width: state.dataWidth + step,
				height: state.dataHeight + step,
			}

			const maxDimensions = boundingRect(props.productsToShow, props.images)

			if ((newDimensions.width < maxDimensions.width || newDimensions.height < maxDimensions.height) && step < 0) return null

			if ((newDimensions.width > 11 * this.state.stageWidth || newDimensions.height > 11 * this.state.stageHeight) && step > 0) return null

			return {
				dataWidth: newDimensions.width,
				dataHeight: newDimensions.height,
			}
		});
	}

	render() {
		const { state, props } = this;

		const scaleX = this.state.stageWidth / state.dataWidth
		const scaleY = this.state.stageHeight / state.dataHeight
		const scale = Math.min(scaleX, scaleY)

		const shown = [];

		const images = props.productsToShow.map(prd => {
			const imgConf = actions.createImageConfigForProduct(prd);
			const img = props.images.get(imgConf.path);
			if (!img || shown.includes(imgConf.path)) return null;
			shown.push(imgConf.path);

			return <PartImage
				key={prd.id}
				image={img.image}
				x={imgConf.img_pos.x * scale}
				y={imgConf.img_pos.y * scale}
				offsetX={img.offsetX * scale}
				offsetY={img.offsetY * scale}
				z={+imgConf.img_pos.z}
				scale={scale}
				color={{
					name: prd.colorName,
					color: prd.color,
					font_color: prd.fontColor,
					selection_color: prd.selectionColor,
					selectionBrighten: `${prd.selectionBrighten}`,
					bg_color: prd.bgColor,
					fileName: props.colors[prd.color],
					path: `${props.config.imgPath}colors/${props.colors[prd.color]}`
				}}
				imageIndex={prd.id}
				selected={getSelectedImagePath() === imgConf.path}
				allowMove={props.appMode === AppMode.Editor}
				navType={props.navType}
				onSelect={props.select}
				onMove={props.updateImgPos}
			/>
		})

		const offsetX = (this.state.stageWidth - state.dataWidth * scale) / 2;
		const offsetY = (this.state.stageHeight - state.dataHeight * scale) / 2;

		return <>
			<div id="kr-canvas" ref={node => { this.stageWrapper = node }} style={{ backgroundColor: `#${props.bgColor}` }}>
				<Stage width={this.state.stageWidth} height={this.state.stageHeight} x={offsetX} y={offsetY} ref={node => { this.stage = node }}>
					<Layer>
						{images}
					</Layer>
				</Stage>
			</div>
		</>
	}

	componentDidMount() {
		this.stageWrapper.onselectstart = () => false // not supported by React in render()
		this.setState({
			stageWidth: this.stageWrapper.clientWidth,
			stageHeight: this.stageWrapper.clientHeight
		})
		if (this.props.appMode === AppMode.Editor) {
			this.stage.container().addEventListener("wheel", this.onWheel, { passive: false })
		}
	}
	
	componentWillUnmount() {
		if (this.props.appMode === AppMode.Editor) {
			this.stage.container().removeEventListener("wheel", this.onWheel)
		}
	}
}

const mapStateToProps = (state: AppState) => ({
	appMode: state.config.appMode,
	mouseMultiplier: state.config.editorModeMouseWheelMultiplier,
	system: state.filteredAssortment.products,
	color: state.filteredAssortment.products[0].colorName,
	navType: state.navType,
	images: state.images,
	products: state.assortment.products,
	productsToShow: state.filteredAssortment.products,
	colors: state.assortment.colors,
	config: state.config,
	selected: state.selected,
	bgColor: state.filteredAssortment.products[0].bgColor
})


const mapDispatchToProps = dispatch => ({
	select: (id: number) => {
		dispatch(actions.selectArticleAction(id))
	},
	updateImgPos: (id: number, imgPos: ImagePosition, scale: number) => {
		const pos = { x: imgPos.x / scale, y: imgPos.y / scale, z: imgPos.z }
		dispatch(actions.updateImgPos(id, pos))
	}
})

export default connect(mapStateToProps, mapDispatchToProps)(PartView)
